import { useState } from "react";
import { 
  INSTAGRAM_URL, 
  WHATSAPP_URL, 
  SUPPORT_EMAIL,
  FACEBOOK_URL,
  TWITTER_URL,
  PINTEREST_URL,
  YOUTUBE_URL
} from "../constants/links";
import { 
  FaWhatsapp, 
  FaInstagram, 
  FaFacebookF, 
  FaTwitter, 
  FaPinterestP, 
  FaYoutube,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  // FaPaperPlane,
  FaCheckCircle,
  FaChevronDown
} from "react-icons/fa";

import {
  Send,
  Person,
  Email,
  Phone,
  Subject,
  Message
} from "@mui/icons-material";
import { HiMail } from "react-icons/hi";
import { BiSupport } from "react-icons/bi";
import { MdOutlineContactSupport } from "react-icons/md";
import { IoLocationSharp } from "react-icons/io5";
import axios from "axios";
import "./Contact.scss";

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false,
    message: ''
  });

  const [activeFaq, setActiveFaq] = useState(null);

  const faqs = [
    {
      question: "How long does shipping take?",
      answer: "Domestic shipping typically takes 3-7 business days. International shipping can take 7-14 business days depending on your location."
    },
    {
      question: "What is your return policy?",
      answer: "We offer 7-day easy returns for all unused items in original packaging."
    },
    {
      question: "Do you ship internationally?",
      answer: "Yes, we ship to most countries worldwide. Shipping charges and delivery times vary by location."
    },
    
    {
  question: "What payment methods do you accept?",
  answer: "Currently, orders are placed via WhatsApp, so payments are made directly there using UPI. We are working on integrating online payment options soon. We will also accept all major credit cards, debit cards, Net Banking, and Cash on Delivery once the integration is live."
},
    {
      question: "How do I cancel my order?",
      answer: "Orders can be cancelled within 24 hours of placement. Contact our support team immediately for cancellation requests."
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ ...formStatus, submitting: true, error: false });

    try {
      const response = await axios.post(`${API}/api/contact`, formData);
      
      if (response.data.success) {
        setFormStatus({
          submitting: false,
          success: true,
          error: false,
          message: 'Message sent successfully! We\'ll get back to you soon.'
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setFormStatus(prev => ({ ...prev, success: false }));
        }, 5000);
      }
    } catch (error) {
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: error.response?.data?.message || 'Something went wrong. Please try again.'
      });

      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, error: false }));
      }, 5000);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Get in Touch</h1>
          <p className="hero-subtitle">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="info-cards-section">
        <div className="container">
          <div className="info-cards-grid">
            {/* Phone Card */}
            <div className="info-card">
              <div className="card-icon">
                <FaPhone />
              </div>
              <h3>Call Us</h3>
              <p>Mon - Sat, 10am - 7pm</p>
              <a href="tel:+919834704067" className="card-link">+91 9834704067</a>
            </div>

            {/* Email Card */}
            <div className="info-card">
              <div className="card-icon">
                <HiMail />
              </div>
              <h3>Email Us</h3>
              <p>24/7 Support Available</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="card-link">{SUPPORT_EMAIL}</a>
            </div>

            {/* WhatsApp Card */}
            <div className="info-card">
              <div className="card-icon whatsapp">
                <FaWhatsapp />
              </div>
              <h3>WhatsApp</h3>
              <p>Quick Response Within 1hr</p>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="card-link">
                Chat Now
              </a>
            </div>

            {/* Location Card */}
            <div className="info-card">
              <div className="card-icon">
                <IoLocationSharp />
              </div>
              <h3>Visit Us</h3>
              <p>By Appointment Only</p>
              <a
    href="https://maps.google.com/?q=Jalaram Terrace Ring Road Surat"
    target="_blank"
    rel="noopener noreferrer"
    className="card-link"
  >
    Surat, India
  </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <div className="section-header">
                <h2>Send us a Message</h2>
                <p>Fill out the form below and we'll get back to you within 24 hours.</p>
              </div>

             <form onSubmit={handleSubmit} className="space-y-6">

  {/* Row 1 */}
  <div className="grid md:grid-cols-2 gap-4">

    {/* Name */}
    <div className="relative">
      <Person className="absolute left-3 top-3 text-gray-400" />
      <input
        type="text"
        name="name"
        placeholder="Your Name *"
        value={formData.name}
        onChange={handleChange}
        required
        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-black outline-none"
      />
    </div>

    {/* Email */}
    <div className="relative">
      <Email className="absolute left-3 top-3 text-gray-400" />
      <input
        type="email"
        name="email"
        placeholder="Your Email *"
        value={formData.email}
        onChange={handleChange}
        required
        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-black outline-none"
      />
    </div>

  </div>


  {/* Row 2 */}
  <div className="grid md:grid-cols-2 gap-4">

    {/* Phone */}
    <div className="relative">
      <Phone className="absolute left-3 top-3 text-gray-400" />
      <input
        type="tel"
        name="phone"
        placeholder="Phone Number"
        value={formData.phone}
        onChange={handleChange}
        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-black outline-none"
      />
    </div>

    {/* Subject */}
    <div className="relative">
      <Subject className="absolute left-3 top-3 text-gray-400" />
      <input
        type="text"
        name="subject"
        placeholder="Subject *"
        value={formData.subject}
        onChange={handleChange}
        required
        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-black outline-none"
      />
    </div>

  </div>


  {/* Message */}
  <div className="relative">
    <Message className="absolute left-3 top-3 text-gray-400" />
    <textarea
      name="message"
      rows="5"
      placeholder="Your Message *"
      value={formData.message}
      onChange={handleChange}
      required
      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-black outline-none resize-none"
    ></textarea>
  </div>


  {/* Button */}
  <button
    type="submit"
    disabled={formStatus.submitting}
    className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition"
  >
    {formStatus.submitting ? (
      "Sending..."
    ) : (
      <>
        Send Message <Send />
      </>
    )}
  </button>


  {/* Success */}
  {formStatus.success && (
    <div className="flex items-center gap-2 text-green-600">
      <FaCheckCircle />
      {formStatus.message}
    </div>
  )}

  {/* Error */}
  {formStatus.error && (
    <div className="text-red-600">
      {formStatus.message}
    </div>
  )}

</form>

<p className="form-trust">
  We usually reply within 24 hours. Your information is safe with us.
</p>
            </div>

            {/* Business Hours & Social Links */}
            <div className="contact-info-wrapper">
              {/* Business Hours */}
              <div className="business-hours">
                <h3>
                  <FaClock /> Business Hours
                </h3>
                <div className="hours-list">
                  <div className="hour-item">
                    <span>Monday - Saturday</span>
                    <span>10:00 AM - 7:00 PM</span>
                  </div>
                  
                  <div className="hour-item">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                  <div className="hour-item highlight">
                    <span>Customer Support</span>
                    <span>24/7 via Email</span>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="social-links-section">
                <h3>
                  <BiSupport /> Connect With Us
                </h3>
                <div className="social-grid">
                  <a 
                    href={INSTAGRAM_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link instagram"
                  >
                    <FaInstagram />
                    <span>Instagram</span>
                  </a>

                  <a 
                    href={FACEBOOK_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link facebook"
                  >
                    <FaFacebookF />
                    <span>Facebook</span>
                  </a>

                  <a 
                    href={TWITTER_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link twitter"
                  >
                    <FaTwitter />
                    <span>Twitter</span>
                  </a>

                  <a 
                    href={PINTEREST_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link pinterest"
                  >
                    <FaPinterestP />
                    <span>Pinterest</span>
                  </a>

                  <a 
                    href={YOUTUBE_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link youtube"
                  >
                    <FaYoutube />
                    <span>YouTube</span>
                  </a>

                  <a 
                    href={WHATSAPP_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-link whatsapp"
                  >
                    <FaWhatsapp />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>

              {/* Quick Support */}
              <div className="quick-support">
                <h3>
                  <MdOutlineContactSupport /> Quick Support
                </h3>
                <p>For urgent queries, reach out to us directly:</p>
                <div className="support-contacts">
                  <a href="tel:+911234567890">
                    <FaPhone /> +91 9834704067
                  </a>
                  <a href={`mailto:${SUPPORT_EMAIL}`}>
                    <FaEnvelope /> {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header centered">
            <h2>Frequently Asked Questions</h2>
            <p>Find answers to common questions about our products and services.</p>
          </div>

          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                onClick={() => setActiveFaq(activeFaq === index ? null : index)}
              >
                <div className="faq-question">
                  <h3>{faq.question}</h3>
                  <FaChevronDown className={`faq-icon ${activeFaq === index ? 'rotated' : ''}`} />
                </div>
                <div className={`faq-answer ${activeFaq === index ? 'show' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
<div className="contact-cta">
  <h3>Still need help?</h3>
 <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
  <FaWhatsapp/> Chat with us on WhatsApp
</a>
</div>
      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <div className="section-header centered">
            <h2>Find Us Here</h2>
            <p>Visit our office location.</p>
          </div>

          <div className="map-container">
            <iframe
              title="Office Location"
             src="https://www.google.com/maps?q=Jalaram%20Terrace%20Ring%20Road%20Surat&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>

            <div className="map-overlay-card">
              <h4>Visit Our Office</h4>
              <p>
                <FaMapMarkerAlt />
                Office No - 34, JALARAM TERRACE, nr. KADIWALA SCHOOL, Ring Road, Surat, Gujarat 395002
              </p>
              <p>
                <FaClock />
                Mon - Sat: 10:00 AM - 7:00 PM
              </p>
             <a
  href="https://maps.google.com/?q=Jalaram Terrace Ring Road Surat"
  target="_blank"
  rel="noopener noreferrer"
  className="directions-btn"
>
  Get Directions →
</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;